import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiChevronDown,
  mdiClockOutline,
  mdiFileDocumentOutline,
  mdiHistory,
  mdiMinusCircleOutline,
  mdiTrashCanOutline,
  mdiTrayArrowDown
} from "@mdi/js";
import Icon from "@mdi/react";
import { Tab } from "@headlessui/react";
import clsx from "clsx";
import dayjs from "dayjs";

import Button from "components/Button";
import Header from "components/Header";
import { RefLoadingSpinner } from "components/LoadingSpinner";
import saveCsvFile from "helpers/saveCsvFile";
import { showErrorMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import {
  getOffPlatformReceivablesUploadAPI,
  getOffPlatformReceivablesUploadsAPI,
  ReceivableUploadResponse,
  ReceivableUploadRowResponse,
  ReceivableUploadRowStatus,
  uploadOffPlatformReceivablesAPI
} from "services/api/offPlatformReceivables";
import downloadReceivablesCsv from "./downloadReceivablesCsv";
import pollUploadStatus from "./pollUploadStatus";
import { downloadTemplateOnNative, TEMPLATE_FILENAME, TEMPLATE_HREF } from "./receivablesTemplate";
import ReceivablesDropzone from "./ReceivablesDropzone";
import ReceivablesList, { ReceivablesListFilters } from "./ReceivablesList";
import styles from "./style.module.scss";
import { buildUploadResultsCsv } from "./uploadResultsCsv";

interface ReceivableUploadRow {
  id: string;
  rowNumber: number;
  invoiceNumber: string;
  loadNumber: string;
  paymentNumber: string;
  status: ReceivableUploadRowStatus;
  detail: string;
}

interface ReceivableUploadHistoryItem {
  id: string;
  created: string;
  filename: string;
  appliedCount: number;
  skippedCount: number;
  erroredCount: number;
  fileUrl?: string;
  rowResults: ReceivableUploadRow[];
  rowResultsCount: number;
  rowResultsTruncated: boolean;
}

interface SelectedReceivablesFile {
  id: string;
  file: File;
}

const HISTORY_ROWS_PAGE_SIZE = 5;
const RECEIVABLES_TAB_INDEX = 2;

const STATUS_META: Record<
  ReceivableUploadRowStatus,
  { label: string; icon: string; className: string }
> = {
  applied: {
    label: "Applied",
    icon: mdiCheckCircleOutline,
    className: styles.statusApplied
  },
  waiting: {
    label: "Waiting",
    icon: mdiClockOutline,
    className: styles.statusWaiting
  },
  errored: {
    label: "Errored",
    icon: mdiAlertCircleOutline,
    className: styles.statusErrored
  },
  skipped: {
    label: "Skipped",
    icon: mdiMinusCircleOutline,
    className: styles.statusSkipped
  }
};

type UploadResultGroupStatus = "errored" | "skipped" | "applied";

const RESULT_GROUP_ORDER: UploadResultGroupStatus[] = ["errored", "skipped", "applied"];

const RESULT_GROUP_ROWS_PAGE_SIZE = 10;

const RESULT_GROUP_SUBTITLES: Record<UploadResultGroupStatus, string> = {
  errored: "Nothing applied — fix these rows and re-upload",
  skipped: "Already reconciled or unchanged — no action taken",
  applied: "Payments posted successfully"
};

const PARTIAL_SUCCESS_ERRORED_SUBTITLE =
  "Fix these rows and re-upload only them — the other rows were applied";

const getResultGroupSubtitle = (status: UploadResultGroupStatus, appliedCount: number) =>
  status === "errored" && appliedCount > 0
    ? PARTIAL_SUCCESS_ERRORED_SUBTITLE
    : RESULT_GROUP_SUBTITLES[status];

const RESULT_GROUP_ACCENT_CLASS: Record<UploadResultGroupStatus, string> = {
  errored: styles.resultsGroupErrored,
  skipped: styles.resultsGroupSkipped,
  applied: styles.resultsGroupApplied
};

const RESULT_GROUP_DETAIL_CLASS: Record<UploadResultGroupStatus, string> = {
  errored: styles.rowDetailErrored,
  skipped: styles.rowDetailSkipped,
  applied: styles.rowDetailApplied
};

const DEFAULT_EXPANDED_RESULT_GROUPS: Record<UploadResultGroupStatus, boolean> = {
  errored: true,
  skipped: true,
  applied: true
};

const mapUploadRows = (rows: ReceivableUploadRowResponse[] = []): ReceivableUploadRow[] =>
  rows.map((row, index) => ({
    id: `${index}-${row.row_number ?? "unknown"}-${row.payment_number}-${row.status}`,
    rowNumber: row.row_number ?? index + 1,
    invoiceNumber: row.invoice_number || "--",
    loadNumber: row.load_number || "--",
    paymentNumber: row.payment_number || "--",
    status: row.status,
    detail: row.detail || "--"
  }));

const mapUploadHistory = (upload: ReceivableUploadResponse): ReceivableUploadHistoryItem => ({
  id: upload.id,
  created: upload.created,
  filename: upload.filename,
  appliedCount: upload.applied_count,
  skippedCount: upload.skipped_count,
  erroredCount: upload.errored_count,
  fileUrl: upload.file_url,
  rowResults: mapUploadRows(upload.row_results),
  rowResultsCount: upload.row_results_count ?? (upload.row_results?.length || 0),
  rowResultsTruncated: upload.row_results_truncated ?? false
});

const STILL_PROCESSING_MESSAGE =
  "This upload is still running in the background. Check the History tab in a few minutes for the final results.";

const buildProgressRow = (
  fileName: string,
  upload?: ReceivableUploadResponse
): ReceivableUploadRow => {
  const processed = upload ? upload.applied_count + upload.skipped_count + upload.errored_count : 0;
  const totalRows = upload?.total_rows || 0;
  // A run that fails after its last row adds a run-level errored entry on top of the per-row counts,
  // so the sum can exceed total_rows. Clamp the display rather than the stored counts: those counts
  // are the audit record of what was actually applied and must not be adjusted to look tidy.
  const processedForDisplay = Math.min(processed, totalRows);
  return {
    id: "uploading",
    rowNumber: 1,
    invoiceNumber: "--",
    loadNumber: fileName,
    paymentNumber: "--",
    status: "waiting",
    detail: totalRows
      ? `Applying ${processedForDisplay.toLocaleString()} of ${totalRows.toLocaleString()} rows`
      : "Queued — waiting to start"
  };
};

const getErrorMessage = (error: unknown, fallback = "Upload failed.") => {
  const maybeError = error as
    | {
        response?: { data?: { detail?: string; row_results?: { detail?: string }[] } };
        message?: string;
      }
    | null
    | undefined;
  const data = maybeError?.response?.data;
  return data?.detail || data?.row_results?.[0]?.detail || maybeError?.message || fallback;
};

const createSelectedFileId = (file: File) => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`;
};

const UPLOAD_RESULTS_CSV_FILENAME = "receivables_upload_results.csv";

const downloadCsvFile = (csvContent: string, filename: string) =>
  saveCsvFile(`${csvContent}\n`, filename);

export default function BrokerReceivables() {
  const [fileList, setFileList] = useState<SelectedReceivablesFile[]>([]);
  const [uploadRows, setUploadRows] = useState<ReceivableUploadRow[]>([]);
  const [expandedResultGroups, setExpandedResultGroups] = useState<
    Record<UploadResultGroupStatus, boolean>
  >(DEFAULT_EXPANDED_RESULT_GROUPS);
  const resultGroupRefs = useRef<Record<UploadResultGroupStatus, HTMLDivElement | null>>({
    errored: null,
    skipped: null,
    applied: null
  });
  const [resultGroupRowLimits, setResultGroupRowLimits] = useState<
    Partial<Record<UploadResultGroupStatus, number>>
  >({});
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const [historyRows, setHistoryRows] = useState<Record<string, ReceivableUploadRow[]>>({});
  const [historyRowsLoading, setHistoryRowsLoading] = useState<Record<string, boolean>>({});
  const [historyRowsError, setHistoryRowsError] = useState<Record<string, string>>({});
  const [historyRowLimits, setHistoryRowLimits] = useState<Record<string, number>>({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [receivablesFilters, setReceivablesFilters] = useState<ReceivablesListFilters>({
    search: "",
    statuses: []
  });
  const [isDownloadingReceivables, setIsDownloadingReceivables] = useState(false);
  const cancelPollRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      cancelPollRef.current?.();
    },
    []
  );

  const fetchUploadHistory = useCallback((params: { limit?: number; offset?: number }) => {
    return getOffPlatformReceivablesUploadsAPI(params);
  }, []);

  const {
    data: uploadHistoryResponse,
    loading: isHistoryLoading,
    error: historyError,
    observerRef: historyObserverRef,
    resetPagination: resetHistoryPagination
  } = useInfiniteScroll<ReceivableUploadResponse>(
    fetchUploadHistory,
    {
      threshold: 0.9,
      limit: 20,
      concatData: true
    },
    ["off-platform-receivables-history"]
  );

  const uploadHistory = useMemo(
    () => uploadHistoryResponse.map(mapUploadHistory),
    [uploadHistoryResponse]
  );
  const historyErrorMessage = historyError ? getErrorMessage(historyError) : "";

  const loadHistoryRows = async (uploadId: string) => {
    if (historyRows[uploadId] || historyRowsLoading[uploadId]) {
      return;
    }
    setHistoryRowsLoading((current) => ({ ...current, [uploadId]: true }));
    setHistoryRowsError((current) => ({ ...current, [uploadId]: "" }));
    try {
      const response = await getOffPlatformReceivablesUploadAPI(uploadId);
      setHistoryRows((current) => ({
        ...current,
        [uploadId]: mapUploadRows(response.data.row_results)
      }));
    } catch (error) {
      setHistoryRowsError((current) => ({
        ...current,
        [uploadId]: getErrorMessage(error, "Could not load row details.")
      }));
    } finally {
      setHistoryRowsLoading((current) => ({ ...current, [uploadId]: false }));
    }
  };

  const toggleHistoryItem = (uploadId: string) => {
    const willExpand = expandedHistoryId !== uploadId;
    setExpandedHistoryId((currentId) => (currentId === uploadId ? null : uploadId));
    setHistoryRowLimits((currentLimits) => ({
      ...currentLimits,
      [uploadId]: currentLimits[uploadId] || HISTORY_ROWS_PAGE_SIZE
    }));
    if (willExpand) {
      loadHistoryRows(uploadId);
    }
  };

  const loadMoreHistoryRows = (uploadId: string) => {
    setHistoryRowLimits((currentLimits) => ({
      ...currentLimits,
      [uploadId]: (currentLimits[uploadId] || HISTORY_ROWS_PAGE_SIZE) + HISTORY_ROWS_PAGE_SIZE
    }));
  };

  const addFile = (file: File) => {
    cancelPollRef.current?.();
    setFileList([{ id: createSelectedFileId(file), file }]);
    setUploadRows([]);
    setResultGroupRowLimits({});
    setErrorMessage("");
    setIsSubmitting(false);
  };

  const removeFile = (fileId: string) => {
    cancelPollRef.current?.();
    setFileList((prev) => prev.filter((fileItem) => fileItem.id !== fileId));
    setUploadRows([]);
    setResultGroupRowLimits({});
    setErrorMessage("");
    setIsSubmitting(false);
  };

  const appliedCount = useMemo(
    () => uploadRows.filter((row) => row.status === "applied").length,
    [uploadRows]
  );
  // Waiting rows are rendered on their own: RESULT_GROUP_ORDER only covers terminal outcomes, so a
  // progress row grouped by outcome would never reach the results list.
  const waitingRows = useMemo(
    () => uploadRows.filter((row) => row.status === "waiting"),
    [uploadRows]
  );
  const waitingCount = waitingRows.length;
  const erroredCount = useMemo(
    () => uploadRows.filter((row) => row.status === "errored").length,
    [uploadRows]
  );
  const skippedCount = useMemo(
    () => uploadRows.filter((row) => row.status === "skipped").length,
    [uploadRows]
  );
  const selectedFileItem = fileList[0];
  const selectedFile = selectedFileItem?.file;
  const selectedFileSize = selectedFile ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : "";

  const resultGroups = useMemo(
    () =>
      RESULT_GROUP_ORDER.map((groupStatus) => ({
        status: groupStatus,
        rows: uploadRows.filter((row) => row.status === groupStatus)
      })).filter((group) => group.rows.length > 0),
    [uploadRows]
  );

  const resultGroupsRowCount = useMemo(
    () => resultGroups.reduce((sum, group) => sum + group.rows.length, 0),
    [resultGroups]
  );

  const toggleResultGroup = (groupStatus: UploadResultGroupStatus) => {
    setExpandedResultGroups((current) => ({
      ...current,
      [groupStatus]: !current[groupStatus]
    }));
  };

  const focusResultGroup = (groupStatus: UploadResultGroupStatus) => {
    if (!uploadRows.some((row) => row.status === groupStatus)) {
      return;
    }
    setExpandedResultGroups((current) => ({ ...current, [groupStatus]: true }));
    requestAnimationFrame(() => {
      resultGroupRefs.current[groupStatus]?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const loadMoreResultRows = (groupStatus: UploadResultGroupStatus) => {
    setResultGroupRowLimits((current) => ({
      ...current,
      [groupStatus]:
        (current[groupStatus] || RESULT_GROUP_ROWS_PAGE_SIZE) + RESULT_GROUP_ROWS_PAGE_SIZE
    }));
  };

  const downloadUploadResults = () => {
    if (!uploadRows.length) {
      return;
    }
    downloadCsvFile(buildUploadResultsCsv(uploadRows), UPLOAD_RESULTS_CSV_FILENAME);
  };

  const refreshHistoryAfterUpload = () => {
    setExpandedHistoryId(null);
    setHistoryRows({});
    setHistoryRowsLoading({});
    setHistoryRowsError({});
    setHistoryRowLimits({});
    resetHistoryPagination().catch(() => undefined);
  };

  const submitUpload = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");
      setUploadRows([buildProgressRow(selectedFile.name)]);
      const response = await uploadOffPlatformReceivablesAPI(selectedFile);
      const acknowledgement = response.data as ReceivableUploadResponse;

      const poll = pollUploadStatus(acknowledgement.id, {
        fetchUpload: async (uploadId) => (await getOffPlatformReceivablesUploadAPI(uploadId)).data,
        onProgress: (upload) => setUploadRows([buildProgressRow(selectedFile.name, upload)])
      });
      cancelPollRef.current = poll.cancel;
      const outcome = await poll.promise;
      cancelPollRef.current = null;

      if (outcome.kind === "cancelled") {
        return;
      }
      if (outcome.kind === "timedOut") {
        setErrorMessage(STILL_PROCESSING_MESSAGE);
        refreshHistoryAfterUpload();
        return;
      }

      const upload = outcome.upload;
      setUploadRows(mapUploadRows(upload.row_results));
      setResultGroupRowLimits({});
      if (upload.status === "errored") {
        setErrorMessage(
          upload.row_results?.find((row) => row.status === "errored")?.detail || "Upload failed."
        );
      }
      refreshHistoryAfterUpload();
    } catch (error) {
      const message = getErrorMessage(error);
      setErrorMessage(message);
      const upload = (error as { response?: { data?: ReceivableUploadResponse } }).response?.data;
      setResultGroupRowLimits({});
      setUploadRows(
        upload?.row_results?.length
          ? mapUploadRows(upload.row_results)
          : [
              {
                id: "upload-error",
                rowNumber: 1,
                invoiceNumber: "--",
                loadNumber: selectedFile.name,
                paymentNumber: "--",
                status: "errored",
                detail: message
              }
            ]
      );
      if (upload?.id) {
        refreshHistoryAfterUpload();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadReceivables = async () => {
    try {
      setIsDownloadingReceivables(true);
      await downloadReceivablesCsv(receivablesFilters);
    } catch (error) {
      showErrorMessage(getErrorMessage(error, "Download failed."));
    } finally {
      setIsDownloadingReceivables(false);
    }
  };

  return (
    <MainLayout
      desktopTitle="Receivables"
      customHeader={<Header label="Receivables" isHideDesktop />}
      className={clsx(styles.receivablesContainer, "desktop:!mx-auto")}>
      <div className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Finance</span>
        </div>
        {selectedTabIndex === RECEIVABLES_TAB_INDEX ? (
          <button
            type="button"
            className={styles.templateButton}
            disabled={isDownloadingReceivables}
            onClick={downloadReceivables}>
            <Icon path={mdiTrayArrowDown} size={0.85} />
            <span>{isDownloadingReceivables ? "Downloading..." : "Download"}</span>
          </button>
        ) : (
          <a
            href={TEMPLATE_HREF}
            target="_blank"
            rel="noopener noreferrer"
            download={TEMPLATE_FILENAME}
            onClick={downloadTemplateOnNative}
            className={styles.templateButton}>
            <Icon path={mdiTrayArrowDown} size={0.85} />
            <span>Template</span>
          </a>
        )}
      </div>

      <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
        <Tab.List className={styles.tabList}>
          {["Upload", "History", "Receivables"].map((tabLabel) => (
            <Tab
              key={tabLabel}
              className={({ selected }) =>
                clsx(styles.tabButton, selected && styles.tabButtonActive)
              }>
              {tabLabel}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          <Tab.Panel className={styles.tabPanel}>
            <div className={styles.workspace}>
              <section className={styles.uploadPanel}>
                <div className={styles.uploaderGrid}>
                  <div className={styles.uploaderWrap}>
                    <ReceivablesDropzone onFileSelect={addFile} />
                  </div>
                  <div className={styles.filePanel}>
                    <span className={styles.filePanelLabel}>Selected File</span>
                    {selectedFile ? (
                      <div className={styles.selectedFile}>
                        <span className={styles.selectedFileIcon}>
                          <Icon path={mdiFileDocumentOutline} size={0.9} />
                        </span>
                        <div className={styles.selectedFileText}>
                          <span className={styles.selectedFileName}>{selectedFile.name}</span>
                          <span className={styles.selectedFileMeta}>
                            {selectedFileSize} • {selectedFile.type || "Spreadsheet"}
                          </span>
                        </div>
                        <button
                          type="button"
                          className={styles.selectedFileRemove}
                          onClick={() => removeFile(selectedFileItem.id)}
                          aria-label={`Remove ${selectedFile.name}`}>
                          <Icon path={mdiTrashCanOutline} size={0.9} />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.emptyFile}>
                        <Icon path={mdiFileDocumentOutline} size={1.2} />
                        <span>No file selected</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.actionRow}>
                  <Button
                    type="button"
                    rounded
                    disabled={!fileList.length || isSubmitting}
                    loading={isSubmitting}
                    onClick={submitUpload}>
                    Submit Upload
                  </Button>
                </div>
              </section>

              <section className={styles.summaryPanel}>
                <button
                  type="button"
                  className={clsx(
                    styles.summaryCard,
                    styles.summaryCardInteractive,
                    RESULT_GROUP_ACCENT_CLASS.applied
                  )}
                  onClick={() => focusResultGroup("applied")}>
                  <span className={styles.summaryValue}>{appliedCount}</span>
                  <span className={styles.summaryLabel}>Applied</span>
                </button>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>{waitingCount}</span>
                  <span className={styles.summaryLabel}>Waiting</span>
                </div>
                <button
                  type="button"
                  className={clsx(
                    styles.summaryCard,
                    styles.summaryCardInteractive,
                    RESULT_GROUP_ACCENT_CLASS.skipped
                  )}
                  onClick={() => focusResultGroup("skipped")}>
                  <span className={styles.summaryValue}>{skippedCount}</span>
                  <span className={styles.summaryLabel}>Skipped</span>
                </button>
                <button
                  type="button"
                  className={clsx(
                    styles.summaryCard,
                    styles.summaryCardInteractive,
                    RESULT_GROUP_ACCENT_CLASS.errored
                  )}
                  onClick={() => focusResultGroup("errored")}>
                  <span className={styles.summaryValue}>{erroredCount}</span>
                  <span className={styles.summaryLabel}>Errored</span>
                </button>
              </section>
            </div>

            <section className={styles.rowsPanel}>
              <div className={styles.sectionHeader}>
                <div>
                  <h3>Upload Results</h3>
                  <span>{resultGroupsRowCount} rows · grouped by outcome</span>
                </div>
                <button
                  type="button"
                  className={styles.downloadResultsButton}
                  disabled={!uploadRows.length}
                  onClick={downloadUploadResults}>
                  <Icon path={mdiTrayArrowDown} size={0.8} />
                  <span>Download results</span>
                </button>
              </div>
              {errorMessage ? <div className={styles.errorBanner}>{errorMessage}</div> : null}
              {uploadRows.length ? (
                <div className={styles.rowsList}>
                  {waitingRows.map((row) => (
                    <div key={row.id} className={styles.rowItem}>
                      <div className={styles.rowMain}>
                        <div className={styles.rowHeaderLine}>
                          <span className={styles.rowTitle}>{row.loadNumber}</span>
                        </div>
                        <span className={styles.rowResultDetail}>{row.detail}</span>
                      </div>
                      <span className={clsx(styles.statusBadge, STATUS_META.waiting.className)}>
                        <Icon path={STATUS_META.waiting.icon} size={0.7} />
                        {STATUS_META.waiting.label}
                      </span>
                    </div>
                  ))}
                  {resultGroups.map((group) => {
                    const statusMeta = STATUS_META[group.status];
                    const isExpanded = expandedResultGroups[group.status];
                    const rowLimit =
                      resultGroupRowLimits[group.status] || RESULT_GROUP_ROWS_PAGE_SIZE;
                    const visibleRows = group.rows.slice(0, rowLimit);
                    const hiddenRowCount = Math.max(group.rows.length - visibleRows.length, 0);
                    return (
                      <div
                        key={group.status}
                        ref={(el) => {
                          resultGroupRefs.current[group.status] = el;
                        }}
                        className={clsx(
                          styles.resultsGroup,
                          RESULT_GROUP_ACCENT_CLASS[group.status]
                        )}>
                        <button
                          type="button"
                          className={styles.resultsGroupHeader}
                          onClick={() => toggleResultGroup(group.status)}
                          aria-expanded={isExpanded}>
                          <span className={styles.resultsGroupTitle}>
                            <span className={clsx(styles.resultsGroupIcon, statusMeta.className)}>
                              <Icon path={statusMeta.icon} size={0.7} />
                            </span>
                            <span className={styles.resultsGroupLabel}>{statusMeta.label}</span>
                            <span className={styles.resultsGroupSubtitle}>
                              {getResultGroupSubtitle(group.status, appliedCount)}
                            </span>
                          </span>
                          <span className={styles.resultsGroupMeta}>
                            <span className={clsx(styles.resultsGroupCount, statusMeta.className)}>
                              {group.rows.length}
                            </span>
                            <Icon
                              path={mdiChevronDown}
                              size={0.8}
                              className={clsx(
                                styles.resultsGroupChevron,
                                !isExpanded && styles.resultsGroupChevronCollapsed
                              )}
                            />
                          </span>
                        </button>
                        {isExpanded ? (
                          <div className={styles.resultsGroupBody}>
                            {visibleRows.map((row) => (
                              <div key={row.id} className={styles.rowItem}>
                                <div className={styles.rowMain}>
                                  <div className={styles.rowHeaderLine}>
                                    <span className={styles.rowPill}>Row {row.rowNumber}</span>
                                    <span className={styles.rowTitle}>
                                      Invoice {row.invoiceNumber}
                                    </span>
                                  </div>
                                  <div className={styles.rowMetaLine}>
                                    <span className={styles.rowDetail}>
                                      Load <strong>{row.loadNumber}</strong>
                                    </span>
                                    <span className={styles.rowDetail}>
                                      Payment <strong>{row.paymentNumber}</strong>
                                    </span>
                                  </div>
                                  <span
                                    className={clsx(
                                      styles.rowResultDetail,
                                      RESULT_GROUP_DETAIL_CLASS[group.status]
                                    )}>
                                    {row.detail}
                                  </span>
                                </div>
                                <span className={clsx(styles.statusBadge, statusMeta.className)}>
                                  <Icon path={statusMeta.icon} size={0.7} />
                                  {statusMeta.label}
                                </span>
                              </div>
                            ))}
                            {hiddenRowCount ? (
                              <div className={styles.resultsGroupFooter}>
                                <span>
                                  Showing {visibleRows.length} of {group.rows.length} rows
                                </span>
                                <button
                                  type="button"
                                  className={styles.resultsGroupLoadMore}
                                  onClick={() => loadMoreResultRows(group.status)}>
                                  Load More
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.emptyState}>Results will appear after submission.</div>
              )}
            </section>
          </Tab.Panel>

          <Tab.Panel className={styles.tabPanel}>
            <section className={styles.historyPanel}>
              {historyErrorMessage ? (
                <div className={styles.errorBanner}>{historyErrorMessage}</div>
              ) : null}
              {uploadHistory.length ? (
                <div className={styles.historyList}>
                  {uploadHistory.map((item) => {
                    const isExpanded = expandedHistoryId === item.id;
                    const visibleRowLimit = historyRowLimits[item.id] || HISTORY_ROWS_PAGE_SIZE;
                    const resolvedRows = historyRows[item.id] || item.rowResults;
                    const visibleRows = resolvedRows.slice(0, visibleRowLimit);
                    const hiddenRowCount = Math.max(resolvedRows.length - visibleRows.length, 0);
                    const isLoadingRows = historyRowsLoading[item.id];
                    const rowsError = historyRowsError[item.id];

                    return (
                      <div key={item.id} className={styles.historyItem}>
                        <div className={styles.historySummary}>
                          <div className={styles.historyIcon}>
                            <Icon path={mdiHistory} size={0.9} />
                          </div>
                          <div className={styles.historyMain}>
                            <span className={styles.historyTitle}>{item.filename}</span>
                            <span className={styles.historyMeta}>
                              {dayjs(item.created).format("MMM D, YYYY [at] h:mm A")}
                            </span>
                          </div>
                          <div className={styles.historyActions}>
                            <button
                              type="button"
                              className={styles.historyToggle}
                              onClick={() => toggleHistoryItem(item.id)}
                              aria-expanded={isExpanded}>
                              Details
                              <Icon
                                path={mdiChevronDown}
                                size={0.8}
                                className={clsx(isExpanded && styles.historyToggleIconOpen)}
                              />
                            </button>
                            {item.fileUrl ? (
                              <a href={item.fileUrl} className={styles.historyLink}>
                                Download
                              </a>
                            ) : null}
                          </div>
                        </div>

                        {isExpanded ? (
                          <div className={styles.historyDetails}>
                            {isLoadingRows ? (
                              <div className={styles.emptyState}>Loading row details...</div>
                            ) : rowsError ? (
                              <div className={styles.errorBanner}>{rowsError}</div>
                            ) : resolvedRows.length ? (
                              <div className={styles.historyTableWrap}>
                                <table className={styles.historyTable}>
                                  <thead>
                                    <tr>
                                      <th>Row</th>
                                      <th>Invoice</th>
                                      <th>Load</th>
                                      <th>Payment</th>
                                      <th>Result</th>
                                      <th>Detail</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {visibleRows.map((row) => {
                                      const statusMeta = STATUS_META[row.status];
                                      return (
                                        <tr key={row.id}>
                                          <td>{row.rowNumber}</td>
                                          <td>{row.invoiceNumber}</td>
                                          <td>{row.loadNumber}</td>
                                          <td>{row.paymentNumber}</td>
                                          <td>
                                            <span
                                              className={clsx(
                                                styles.statusBadge,
                                                statusMeta.className
                                              )}>
                                              {statusMeta.label}
                                            </span>
                                          </td>
                                          <td>{row.detail}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {hiddenRowCount ? (
                                  <div className={styles.historyTableFooter}>
                                    <span>
                                      Showing {visibleRows.length} of {resolvedRows.length} rows
                                    </span>
                                    <button
                                      type="button"
                                      className={styles.historyLoadMore}
                                      onClick={() => loadMoreHistoryRows(item.id)}>
                                      Load More
                                    </button>
                                  </div>
                                ) : null}
                                {item.rowResultsTruncated ? (
                                  <div className={styles.historyTableFooter}>
                                    <span>
                                      Showing the first {resolvedRows.length} of{" "}
                                      {item.rowResultsCount} rows. Download the file for the full
                                      list.
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <div className={styles.emptyState}>No row details available.</div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  <RefLoadingSpinner
                    observerRef={historyObserverRef}
                    isLoadingMore={isHistoryLoading}
                  />
                </div>
              ) : (
                <div className={styles.emptyState}>
                  {isHistoryLoading ? "Loading upload history." : "No uploads yet."}
                </div>
              )}
            </section>
          </Tab.Panel>

          <Tab.Panel className={styles.tabPanel}>
            <ReceivablesList onFiltersChange={setReceivablesFilters} />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </MainLayout>
  );
}
